/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import { compareSchemaVersions } from '../../utils/compareSchema';
import { minUIDSupportVersion } from '../../queries';
import { categoryWithChildren as categoryWithChildren235, categoryList as categoryList235 } from '../../queries/2.3.5';
import { categoryList as categoryList242, categoryByFilterPagination } from '../../queries/2.4.2ce';
import { event_categorypicker_navigate, event_categorypicker_search } from '../../utils/trackingEvents';
import { trackEvent } from '../../utils/tracking';
import { useConfigContext } from '../ConfigContext/ConfigContext';
import { triggerWhenPickerRenders, triggerOnFilterChange, assignFolderBeenOpened } from '../../utils/pickerElement';

const SELECTION_VALUE_ID = 'id';
const SELECTION_VALUE_SLUG = 'slug';
const SELECTION_VALUE_UID = 'uid';
const SELECTION_VALUE_ID_AND_URL_PATH = 'idAndUrlPath';
const SELECTION_VALUE_UID_AND_URL_PATH = 'uidAndUrlPath';
const SELECTION_VALUE_URL_PATH = 'urlPath';
const INVENTORY_PAGINATION = {
    total: 0,
    page: 1,
    limit: 20
};

export const CategoryPickerContext = React.createContext();

const asString = value => value && String(value);

const mapForInventory = ({ key, name, parentKey, id, uid }) => {
    const defaultMapping = {
        key: asString(key),
        label: name,
        parentKey: asString(parentKey)
    };

    if (id) defaultMapping['id'] = id;
    if (uid) defaultMapping['uid'] = uid;

    return defaultMapping;
};

const computePath = (identifier, categoriesRef) => {
    let path = [];
    let currentItem = { parentKey: identifier };
    while (currentItem && currentItem.parentKey) {
        currentItem = categoriesRef.current.get(currentItem.parentKey);
        if (currentItem) path.unshift(currentItem);
    }

    return path;
};

const resetPaginationEventKeys = new Set(['folder', 'query']);

export const buildFilterQuery = (keys, type, uidSupported) => {
    switch (type) {
        case SELECTION_VALUE_SLUG:
            return { url_key: { in: keys } };
        case SELECTION_VALUE_UID:
            return { category_uid: { in: keys } };
        case SELECTION_VALUE_ID_AND_URL_PATH: {
            const ids = keys.map(key => key.split('|')[0]);
            return { ids: { in: ids } };
        }
        case SELECTION_VALUE_UID_AND_URL_PATH: {
            const uids = keys.map(key => key.split('|')[0]);
            return { category_uid: { in: uids } };
        }
        case SELECTION_VALUE_URL_PATH:
            return { url_path: { in: keys } };
        case SELECTION_VALUE_ID:
            return { ids: { in: keys } };
        default:
            if (uidSupported) {
                return { category_uid: { in: keys } };
            }
            return { ids: { in: keys } };
    }
};

/**
 * Returns the selectionId type based on the actual selection or a fallback based on UID support.
 */
const getSelectionIdType = (uidSupported, selectionId) => {
    if (selectionId) {
        return selectionId;
    }
    if (uidSupported) {
        return SELECTION_VALUE_UID;
    }
    return SELECTION_VALUE_ID;
};

/**
 * Return the key field for a category as used internally by the picker and for selections.
 */
export const getSelectionId = (category, selectionId, uidSupported) => {
    const { url_key, uid, id, url_path, unknown, label } = category;

    selectionId = getSelectionIdType(uidSupported, selectionId);

    // If category is unknown, return the name as value
    if (unknown) {
        return [label, selectionId];
    }

    switch (selectionId) {
        case SELECTION_VALUE_SLUG:
            return [url_key, SELECTION_VALUE_SLUG];
        case SELECTION_VALUE_UID:
            return [uid, SELECTION_VALUE_UID];
        case SELECTION_VALUE_ID_AND_URL_PATH:
            return [`${id}|${url_path}`, SELECTION_VALUE_ID_AND_URL_PATH];
        case SELECTION_VALUE_UID_AND_URL_PATH:
            return [`${uid}|${url_path}`, SELECTION_VALUE_UID_AND_URL_PATH];
        case SELECTION_VALUE_URL_PATH:
            return [url_path, SELECTION_VALUE_URL_PATH];
        default:
            return [`${id}`, SELECTION_VALUE_ID];
    }
};

// i18n
const messages = defineMessages({
    rootCategory: {
        id: 'navigation.rootCategory',
        defaultMessage: 'Root Category'
    }
});

const CategoryPickerContextProvider = ({
    children,
    domRoot,
    webComponentTag,
    treeNavigationPagination = {
        total: 0,
        page: 1,
        limit: 20
    },
    selectionId
}) => {
    const [[config]] = useConfigContext();
    const [schemaStatus, , [gqlConfig]] = useGraphqlSchemaContext();
    const { formatMessage } = useIntl();

    const displayName = config.name ? config.name : formatMessage(messages.rootCategory);
    const rootCategory = { key: String(config.rootCategory), name: displayName };
    const initialState = {
        items: [],
        path: [mapForInventory(rootCategory)],
        loadingState: 'idle',
        paginationSupported: false,
        pagination: INVENTORY_PAGINATION
    };
    const [filterState, setFilterState] = useState({});

    const categoriesRef = useRef(new Map([[rootCategory.key, rootCategory]]));
    const [state, setState] = useReducer((currentState, newState) => ({ ...currentState, ...newState }), initialState);

    const getCategoryListQuery = () => {
        if (compareSchemaVersions(gqlConfig.schemaVersion, minUIDSupportVersion) === -1) {
            return [categoryList235, false];
        }
        return [categoryList242, true];
    };

    const getCategoryWithChildrenQuery = searchRequired => {
        if (compareSchemaVersions(gqlConfig.schemaVersion, minUIDSupportVersion) === -1) {
            if (searchRequired) {
                return [categoryList235, false];
            }
            return [categoryWithChildren235, false];
        }

        return [categoryByFilterPagination, true];
    };

    /**
     * Returns the key used by the Inventory component when a category is selected.
     */
    const getKey = category => {
        // Url path can also be a key
        if (selectionId === SELECTION_VALUE_URL_PATH) {
            return category.url_path;
        } else if (gqlConfig.supportUID) {
            return category.uid;
        }
        return asString(category.id);
    };

    /**
     * It resolves the appropriate identifier for the category that gets expanded in the tree
     */
    const getParentIdentifier = item => {
        if (item.isRootFolder) {
            return config.rootCategory;
        } else if (gqlConfig.supportUID) {
            return item.folder.uid;
        } else {
            return item.folder.id;
        }
    };

    /**
     * The method provides category data for the FolderTree component.
     */
    const onFolderExpand = async item => {
        let identifier = getParentIdentifier(item);

        // Load root category if required
        await triggerOnFilterChange(domRoot, webComponentTag, onFilterChange, state.pagination);

        // This method currently does not support pagination, so we can only retrieve the first page of categories
        const [children] = await getCategoriesForParent(identifier, treeNavigationPagination);

        assignFolderBeenOpened(domRoot, webComponentTag);
        return children.map(mapForInventory);
    };

    const getCategories = async identifiers => {
        let toBeQueried = new Map();

        let categories = identifiers.map((identifier, idx) => {
            // Lookup category from cache
            const stringIdentifier = asString(identifier);
            if (categoriesRef.current.has(stringIdentifier)) {
                return categoriesRef.current.get(stringIdentifier);
            }

            // Store together with the index within the categories array
            toBeQueried.set(stringIdentifier, idx);

            // Mark as unknown for now
            return { key: stringIdentifier, label: stringIdentifier, unknown: true };
        });

        // Get categories that are not cached
        if (toBeQueried.size > 0) {
            const gqlClient = gqlConfig.gqlClient;
            const filters = buildFilterQuery(Array.from(toBeQueried.keys()), selectionId, gqlConfig.supportUID);
            const variables = { filters };

            // We can rely on Apollo caching here to not trigger a new request for unknown categories if entered
            // manually multiple times.
            const [query, paginationSupported] = getCategoryListQuery();
            const result = await gqlClient.query({
                query,
                variables,
                // Ignore any GraphQL errors here, since a user might provide an identifier of invalid format
                errorPolicy: 'ignore'
            });

            // Get categories from result
            let items = [];
            if (!paginationSupported && result.data.categoryList && result.data.categoryList.length > 0) {
                items = result.data.categoryList;
            } else if (paginationSupported && result.data.categories && result.data.categories.items) {
                items = result.data.categories.items;
            }

            // Replace unknown entires in the categories array
            items.forEach(item => {
                const key = getKey(item);
                const [selectionIdKey] = getSelectionId(item, selectionId, gqlConfig.supportUID);
                const idx = toBeQueried.get(selectionIdKey);
                const label = item.url_path ? `${item.name} (${item.url_path})` : item.name;
                const category = {
                    ...item,
                    label,
                    id: asString(item.id),
                    key,
                    selectionIdKey
                };
                categories[idx] = category;
                categoriesRef.current.set(key, category);
            });
        }

        return categories;
    };

    const getSearchFilters = (searchTerm, identifier) => {
        // When a search term is specified, do a generic search and ignore the parent category filter
        if (searchTerm && searchTerm.length > 2) {
            return {
                name: {
                    match: searchTerm
                }
            };
        }

        return {
            [gqlConfig.supportUID ? 'parent_category_uid' : 'parent_id']: {
                eq: String(identifier)
            }
        };
    };

    /**
     * Internal method to retrieve child categories for a given parent category identifier.
     */
    const getCategoriesForParent = async (identifier, pagination, searchTerm) => {
        const searchRequired = searchTerm && searchTerm.length > 2;
        const gqlClient = gqlConfig.gqlClient;
        const [query, paginationSupported] = getCategoryWithChildrenQuery(searchRequired);

        const variables = {
            [gqlConfig.supportUID ? 'uid' : 'id']: [String(identifier)],
            currentPage: pagination.page,
            pageSize: pagination.limit,
            filters: getSearchFilters(searchTerm, identifier)
        };

        const result = await gqlClient.query({
            query,
            variables
        });

        // Get categories from result
        let categories = [];
        let totalCount = 0;
        if (result) {
            if (
                !paginationSupported &&
                result.data.categoryList &&
                result.data.categoryList.length > 0 &&
                result.data.categoryList[0].children
            ) {
                categories = result.data.categoryList[0].children;
                totalCount = result.data.categoryList[0].children.length;
            } else if (!paginationSupported && result.data.categoryList && result.data.categoryList.length > 0) {
                categories = result.data.categoryList;
                totalCount = result.data.categoryList.length;
            } else if (paginationSupported && result.data.categories && result.data.categories.items) {
                categories = result.data.categories.items;
                totalCount = result.data.categories.total_count;
            }
        }

        // Get children and add a key attribute
        categories = categories.map(c => {
            const [selectionIdKey] = getSelectionId(c, selectionId, gqlConfig.supportUID);
            return {
                ...c,
                uid: asString(c.uid),
                id: asString(c.id),
                key: getKey(c),
                label: c.url_path ? `${c.name} (${c.url_path})` : c.name,
                parentKey: asString(identifier),
                selectionIdKey
            };
        });

        // Add new categories to categories map
        // Skip when performing a search, because then we cannot know about the correct parent category
        if (!searchRequired) {
            categories.forEach(child => categoriesRef.current.set(child.key, child));
        }

        return [categories, totalCount, paginationSupported];
    };

    /**
     * It resolves the identifier to be used while getting subcategories from the selected category.
     * Since children are resolved based on parent UID/ID, this is mainly needed while working with URL Paths
     */
    const resolveIdentifierFromInventorySelection = item => {
        if (!item || item === config.rootCategory) return config.rootCategory;

        const category = categoriesRef.current.get(item);
        if (gqlConfig.supportUID) {
            return category.uid;
        } else {
            return category.id;
        }
    };

    /**
     * The method provides category data for the Inventory component.
     */
    const onFilterChange = async ({ folder, pagination, query }, filterEvent) => {
        const identifier = resolveIdentifierFromInventorySelection(folder);
        let trackSearch = false;

        // Don't allow navigating while loading in progress
        if (state.loadingState === 'loading') {
            return;
        }
        setFilterState({ query });

        let newPagination = pagination ? pagination : INVENTORY_PAGINATION;
        // Reset pagination if folder or search term has changed
        if (filterEvent && filterEvent.keys && filterEvent.keys.find(k => resetPaginationEventKeys.has(k.key))) {
            newPagination = INVENTORY_PAGINATION;
            trackSearch = !!filterEvent.keys.find(k => k.key === 'query');
        }

        setState({ items: [], loadingState: 'loading' });

        const [children, totalCount, paginationSupported] = await getCategoriesForParent(
            identifier,
            newPagination,
            query
        );

        // Compute new breadcrumb path
        const newPath = computePath(identifier, categoriesRef).map(mapForInventory);

        const newState = {
            items: children,
            loadingState: 'idle',
            path: newPath,
            paginationSupported,
            filterState: { query }
        };
        if (paginationSupported) {
            newState.pagination = { ...newPagination, total: totalCount };
        }
        setState(newState);

        if (trackSearch) {
            trackEvent(event_categorypicker_search);
        } else {
            trackEvent(event_categorypicker_navigate);
        }
    };

    useEffect(() => {
        if (schemaStatus !== 'done') {
            return;
        }

        // Reset the value when component gets rendered
        triggerWhenPickerRenders(domRoot, webComponentTag, onFilterChange, state.pagination);
    }, [schemaStatus]);

    const api = [
        {
            items: state.items,
            path: state.path,
            loadingState: state.loadingState,
            paginationSupported: state.paginationSupported,
            pagination: state.pagination,
            filterState
        },
        {
            onFolderExpand,
            onFilterChange,
            getCategories,
            getSelectionId,
            getSelectionIdType
        }
    ];

    return <CategoryPickerContext.Provider value={api}>{children}</CategoryPickerContext.Provider>;
};

CategoryPickerContextProvider.propTypes = {
    selectionId: PropTypes.oneOf(['id', 'slug', 'idAndUrlPath', 'uid', 'uidAndUrlPath', 'urlPath']),
    treeNavigationPagination: PropTypes.shape({
        total: PropTypes.number,
        page: PropTypes.number,
        limit: PropTypes.number
    }),
    webComponentTag: PropTypes.string,
    domRoot: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })])
};

export default CategoryPickerContextProvider;

export const useCategoryPickerContext = () => useContext(CategoryPickerContext);
